import React, { useState } from 'react';
import { deleteDoc, doc, updateDoc } from '../../lib/auditedFirestore'; 
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext'; 
import { logAuditAction } from '../../lib/auditLogger';

// Components
import BulkAddStudents from './components/BulkAddStudents';
import AddUserForm from './components/AddUserForm';
import UserList from './components/UserList';
import AuditTrail from './components/AuditTrail';
import PaperListAdmin from './PaperListAdmin';
import ExamListAdmin from './ExamListAdmin';
import CreateExam from './CreateExam';
import ExamAttemptsList from './ExamAttemptsList';
import ExamAnalytics from '../exam/ExamAnalytics';
import QuestionBankManager from './QuestionBankManager';
import LectureManager from '../lecture/LectureManager';
import LectureRoom from '../lecture/LectureRoom';
import HomeworkManager from '../homework/HomeworkManager';
import HomeworkRoom from '../homework/HomeworkRoom';
import InstilIQManager from './InstilIQManager'; // Added Component Import

const ConfirmationModal = ({ message, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full text-center">
            <p className="mb-6 text-gray-700">{message}</p>
            <div className="flex justify-center gap-4">
                <button
                    onClick={onCancel}
                    className="bg-gray-300 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-400"
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600"
                >
                    Confirm
                </button>
            </div>
        </div>
    </div>
);

const AdminDashboard = ({ user, setModal }) => {
    // Access userData to check permissions
    const { userData } = useAuth(); 
    
    // --- Granular RBAC Permission Checks ---
    const isLegacyAdmin = !userData?.allowedFeatures; 
    
    const canManageStudents = isLegacyAdmin || userData?.allowedFeatures?.includes('manage_students');
    const canManageExams = isLegacyAdmin || userData?.allowedFeatures?.includes('manage_exams');
    const canEvaluateAnswers = isLegacyAdmin || userData?.allowedFeatures?.includes('evaluate_answers');
    const canViewReports = isLegacyAdmin || userData?.allowedFeatures?.includes('view_reports');
    
    // A user can view the Exam tab if they have ANY of the content/exam related permissions
    const canAccessExamTab = canManageExams || canEvaluateAnswers || canViewReports;
    
    const canViewAuditLogs = userData?.canViewAuditLogs === true || userData?.allowedFeatures?.includes('view_audit_trail');

    // --- View State ---
    const [view, setView] = useState(() => {
        if (canManageStudents) return 'users';
        if (canAccessExamTab) return 'exams';
        if (canViewAuditLogs) return 'audit';
        return 'none';
    });
    
    // --- Exam Management Sub-Views ---
    const [examMgmtView, setExamMgmtView] = useState('paperList'); 
    
    const [selectedPaper, setSelectedPaper] = useState(null);
    const [selectedExam, setSelectedExam] = useState(null);
    const [editingExam, setEditingExam] = useState(null);
    const [examToDelete, setExamToDelete] = useState(null);
    const [activeLecture, setActiveLecture] = useState(null);
    const [homeworkReviewData, setHomeworkReviewData] = useState(null);

    // --- Protected Handlers ---
    const handleEditExam = (exam) => {
        if (!canManageExams) {
            setModal({ show: true, message: "Permission Denied: You do not have permission to edit exams." });
            return;
        }
        setEditingExam(exam);
        setExamMgmtView('examForm');
    };

    const handleDeleteExamRequest = (examId) => {
        if (!canManageExams) {
            setModal({ show: true, message: "Permission Denied: You do not have permission to delete exams." });
            return;
        }
        setExamToDelete(examId);
    };

    const confirmDeleteExam = async () => {
        if (examToDelete) {
            try {
                await deleteDoc(doc(db, 'exams', examToDelete));
                logAuditAction(user, 'DELETE_EXAM', `Deleted exam with ID: ${examToDelete}`, { id: examToDelete });
                setModal({ show: true, message: 'Exam deleted successfully.' });
            } catch (error) {
                setModal({ show: true, message: `Error deleting exam: ${error.message}` });
                console.error("Error deleting exam:", error);
            } finally {
                setExamToDelete(null);
            }
        }
    };

    const handleCreateNewExam = () => {
        if (!canManageExams) {
            setModal({ show: true, message: "Permission Denied: You do not have permission to create exams." });
            return;
        }
        setEditingExam(null);
        setExamMgmtView('examForm');
    };

    const handleViewExams = (paper) => {
        setSelectedPaper(paper);
        setExamMgmtView('examList');
    };
    
    const handleViewQuestionBank = (paper) => {
        setSelectedPaper(paper);
        setExamMgmtView('questionBank');
    };

    const handleViewLectures = (paper) => {
        setSelectedPaper(paper);
        setExamMgmtView('lectureManager');
    };

    const handleViewHomework = (paper) => {
        setSelectedPaper(paper);
        setExamMgmtView('homeworkManager');
    };

    // Added explicit Instil IQ management callback route 
    const handleViewInstilIQ = (paper) => {
        setSelectedPaper(paper);
        setExamMgmtView('instilIQ');
    };

    const handleStartLiveClass = (lecture) => {
        setActiveLecture(lecture);
        setExamMgmtView('adminLiveRoom');
    };

    const handleViewSubmissions = (exam) => {
        if (!canEvaluateAnswers && !canViewReports && !canManageExams) {
            setModal({ show: true, message: "Permission Denied: You do not have access to view submissions." });
            return;
        }
        setSelectedExam(exam);
        setExamMgmtView('examAttemptsList');
    };

    const handleViewAnalytics = (exam) => {
        if (!canViewReports && !canManageExams) {
            setModal({ show: true, message: "Permission Denied: You do not have access to view analytics." });
            return;
        }
        setSelectedExam(exam);
        setExamMgmtView('examAnalytics');
    };

    const handleAllowRetake = async (attemptId) => {
        if (!canManageStudents && !canManageExams) {
            setModal({ show: true, message: "Permission Denied: You do not have permission to modify attempts." });
            return;
        }
        if (!window.confirm("Allow this student to retake the exam? This will let them start a new attempt.")) return;
        try {
            await updateDoc(doc(db, 'attempts', attemptId), { allowRetake: true });
            logAuditAction(user, 'ALLOW_RETAKE', `Allowed retake for attempt ID: ${attemptId}`, { attemptId });
            setModal({ show: true, message: "Retake allowed for student." });
        } catch (error) {
            console.error("Error allowing retake:", error);
            setModal({ show: true, message: "Failed to allow retake." });
        }
    };

    const renderExamManagement = () => {
        switch (examMgmtView) {
            case 'instilIQ':
                return <InstilIQManager
                            paper={selectedPaper}
                            user={user}
                            onBack={() => { setSelectedPaper(null); setExamMgmtView('paperList'); }}
                            setModal={setModal}
                        />;
            case 'examForm':
                return <CreateExam
                            user={user}
                            setModal={setModal}
                            examToEdit={editingExam}
                            paper={selectedPaper}
                            onFinishEditing={() => { setEditingExam(null); setExamMgmtView('examList'); }}
                        />;
            case 'examAttemptsList':
                return <ExamAttemptsList
                            exam={selectedExam}
                            paper={selectedPaper}
                            onBack={() => { setSelectedExam(null); setExamMgmtView('examList'); }}
                            setModal={setModal}
                            onAllowRetake={handleAllowRetake}
                        />;
            case 'examAnalytics':
                return <ExamAnalytics
                            exam={selectedExam}
                            onBack={() => { setSelectedExam(null); setExamMgmtView('examList'); }}
                            setModal={setModal}
                        />;
            case 'questionBank':
                return <QuestionBankManager
                            paper={selectedPaper}
                            onBack={() => { setSelectedPaper(null); setExamMgmtView('paperList'); }}
                            setModal={setModal}
                        />;
            case 'lectureManager':
                return <LectureManager
                            paper={selectedPaper}
                            onBack={() => { setSelectedPaper(null); setExamMgmtView('paperList'); }}
                            setModal={setModal}
                            onStartLive={handleStartLiveClass} 
                        />;
            case 'homeworkManager':
                return <HomeworkManager
                            paper={selectedPaper}
                            onBack={() => { setSelectedPaper(null); setExamMgmtView('paperList'); }}
                            setModal={setModal}
                            onOpenHomework={(hw, student, sub) => {
                                setHomeworkReviewData({ hw, student, sub });
                                setExamMgmtView('homeworkReview');
                            }}
                        />;
            case 'homeworkReview':
                return <HomeworkRoom
                            homework={homeworkReviewData.hw}
                            user={user} 
                            onBack={() => setExamMgmtView('homeworkManager')}
                            reviewMode={true}
                            reviewTargetStudent={homeworkReviewData.student}
                            reviewSubmission={homeworkReviewData.sub}
                        />;
            case 'adminLiveRoom':
                return <LectureRoom 
                            lecture={activeLecture} 
                            user={user} 
                            userRole={userData?.role}
                            mode="admin-live" 
                            onBack={() => { 
                                setActiveLecture(null); 
                                setExamMgmtView('lectureManager'); 
                            }} 
                        />;
            case 'examList':
                return <ExamListAdmin
                            paper={selectedPaper}
                            onEdit={handleEditExam}
                            onDelete={handleDeleteExamRequest}
                            onCreateNew={handleCreateNewExam}
                            onViewSubmissions={handleViewSubmissions}
                            onViewAnalytics={handleViewAnalytics} 
                            onBack={() => { setSelectedPaper(null); setExamMgmtView('paperList'); }}
                            setModal={setModal}
                        />;
            case 'paperList':
            default:
                return <PaperListAdmin 
                    onViewExams={handleViewExams} 
                    onViewQuestionBank={handleViewQuestionBank} 
                    onViewLectures={handleViewLectures}
                    onViewHomework={handleViewHomework} 
                    onViewInstilIQ={handleViewInstilIQ} // Attached to child triggers
                    setModal={setModal}
                    allowedFeatures={userData?.allowedFeatures}
                    assignedPapers={userData?.assignedPapers}
                />;
        }
    };

    return (
        <div>
            {examToDelete && (
                <ConfirmationModal
                    message="Are you sure you want to delete this exam? This action cannot be undone."
                    onConfirm={confirmDeleteExam}
                    onCancel={() => setExamToDelete(null)}
                />
            )}
            
            <div className="flex gap-4 mb-6 border-b overflow-x-auto p-1">
                {canManageStudents && (
                    <button 
                        onClick={() => setView('users')} 
                        className={`py-2 px-4 font-semibold whitespace-nowrap rounded-t-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md ${
                            view === 'users' 
                            ? 'border-b-2 border-purple-600 text-purple-600 bg-purple-50' 
                            : 'text-gray-500 hover:text-purple-500 hover:bg-gray-50'
                        }`}
                    >
                        Student Management
                    </button>
                )}
                
                {canAccessExamTab && (
                    <button 
                        onClick={() => setView('exams')} 
                        className={`py-2 px-4 font-semibold whitespace-nowrap rounded-t-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md ${
                            view === 'exams' 
                            ? 'border-b-2 border-purple-600 text-purple-600 bg-purple-50' 
                            : 'text-gray-500 hover:text-purple-500 hover:bg-gray-50'
                        }`}
                    >
                        Exam & Content Management
                    </button>
                )}
                
                {canViewAuditLogs && (
                    <button 
                        onClick={() => setView('audit')} 
                        className={`py-2 px-4 font-semibold whitespace-nowrap rounded-t-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md ${
                            view === 'audit' 
                            ? 'border-b-2 border-purple-600 text-purple-600 bg-purple-50' 
                            : 'text-gray-500 hover:text-purple-500 hover:bg-gray-50'
                        }`}
                    >
                        Audit Logs
                    </button>
                )}
            </div>

            {view === 'none' && (
                <div className="text-center mt-12 p-8 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-2xl font-bold text-gray-700">No Permissions Assigned</h3>
                    <p className="text-gray-500 mt-2">You do not currently have access to any dashboard modules.</p>
                    <p className="text-gray-500">Please contact the Super Admin to configure your account.</p>
                </div>
            )}

            {view === 'users' && canManageStudents && (
                <div className="space-y-8 animate-fade-in">
                    <BulkAddStudents addedBy={user.uid} setModal={setModal} />
                    <AddUserForm roleToAdd="student" addedBy={user.uid} setModal={setModal} />
                    <UserList 
                        roleToList="student" 
                        setModal={setModal} 
                        assignedPapers={userData?.assignedPapers} 
                    />
                </div>
            )}
            
            {view === 'exams' && canAccessExamTab && (
                <div className="animate-fade-in">
                    {renderExamManagement()}
                </div>
            )}

            {view === 'audit' && canViewAuditLogs && (
                <div className="animate-fade-in">
                    <AuditTrail />
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;